import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
  description?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder,
  className,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const removeOption = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  };

  const getOption = (value: string) => options.find((opt) => opt.value === value);
  const getLabel = (value: string) => getOption(value)?.label || value;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-border rounded-md bg-background hover:bg-accent transition-colors min-h-[40px]"
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{placeholder ?? t('common.selectPlaceholder')}</span>
          ) : (
            selected.map((value) => (
              <span
                key={value}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-primary text-sm"
                title={getOption(value)?.description || getLabel(value)}
              >
                {getLabel(value)}
                <X
                  className="w-3 h-3 hover:text-destructive cursor-pointer"
                  onClick={(e) => removeOption(value, e)}
                />
              </span>
            ))
          )}
        </div>
        <ChevronsUpDown className="w-4 h-4 ml-2 text-muted-foreground shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 border border-border rounded-md bg-popover shadow-lg max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t('common.noOptions')}
            </div>
          ) : (
            options.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <div
                  key={option.value}
                  onClick={() => toggleOption(option.value)}
                  className={cn(
                    'flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-accent transition-colors',
                    isSelected && 'bg-accent/50'
                  )}
                >
                  <div className="min-w-0">
                    <div className="text-sm">{option.label}</div>
                    {option.description ? (
                      <div className="text-xs text-muted-foreground truncate">
                        {option.description}
                      </div>
                    ) : null}
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-primary" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
